import { useEffect, useRef, useState } from 'react';

/**
 * PromptDialog — reusable single-input modal.
 *
 * Used wherever the native window.prompt() is too ugly / not accessible
 * (rename folder, rename template, "clone as…" name, new folder name).
 * Keyboard behaviour:
 *   - Enter submits if the input is non-empty and different from the
 *     original when one is supplied.
 *   - Escape cancels.
 *   - Focus traps inside the dialog; the input is auto-focused and
 *     pre-selected so the first keystroke replaces the suggested name.
 */
export interface PromptDialogProps {
  title: string;
  /** Short description / context, rendered above the input. */
  description?: string | undefined;
  label: string;
  initialValue?: string | undefined;
  placeholder?: string | undefined;
  submitLabel?: string | undefined;
  /** Optional inline validator. Return a string to show as an error,
   *  or null to allow submission. */
  validate?: ((value: string) => string | null) | undefined;
  /** Called with the trimmed value. Resolve to close; throw to surface
   *  an error that replaces the inline validation error. */
  onSubmit(value: string): void | Promise<void>;
  onCancel(): void;
  destructive?: boolean | undefined;
}

export function PromptDialog(props: PromptDialogProps): JSX.Element {
  const {
    title,
    description,
    label,
    initialValue = '',
    placeholder,
    submitLabel = 'Save',
    validate,
    onSubmit,
    onCancel,
    destructive = false,
  } = props;

  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Focus + select on mount.
    const el = inputRef.current;
    if (el) {
      el.focus();
      el.select();
    }
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const trimmed = value.trim();
  const unchanged = trimmed === initialValue.trim();
  const validationError = validate ? validate(trimmed) : null;
  const canSubmit = !busy && trimmed.length > 0 && !validationError && !unchanged;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError((err as Error).message || 'Save failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <form
        className="modal modal--prompt"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        aria-labelledby="prompt-title"
      >
        <header className="modal-header">
          <div>
            <h2 id="prompt-title">{title}</h2>
            {description && <p className="modal-subtitle">{description}</p>}
          </div>
          <button
            type="button"
            className="btn-icon"
            onClick={onCancel}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <div className="modal-body">
          <label className="form-label" htmlFor="prompt-input">
            {label}
          </label>
          <input
            id="prompt-input"
            ref={inputRef}
            className="form-input"
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck="false"
          />
          {validationError && !error && (
            <div className="form-hint form-hint--error">{validationError}</div>
          )}
          {error && <div className="form-hint form-hint--error">{error}</div>}
        </div>
        <footer className="modal-footer">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className={`btn ${destructive ? 'btn--danger' : 'btn--primary'}`}
            disabled={!canSubmit}
          >
            {busy ? 'Saving…' : submitLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}
